import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { reportError } from '@mc/next-newrelic'
import { getHelpCenterAnonUuidCookie } from '../helpCenterUuidCookie'
import { useChatAvailability, useWidgetHandler } from '../hooks'
import { useDecagon } from '../hooks/useDecagon'
import { BrandKey, brandKeysByRoute } from '../utils'

const DECAGON_AI_SCRIPT_SRC = 'https://decagon.ai/loaders/masterclass.js'

export interface DecagonUserInfo {
  userId: string
  email?: string
  is_logged_in: boolean
  platform_name: string
  device: string
  country: string
  region: string
  current_help_center_product?: string
}
interface DecagonAiProps {
  userAttributes: DecagonUserInfo
}

// CHAT SETUP
const setupChat = ({
  userId,
  metadata,
}: {
  userId: string
  metadata: Record<string, string | boolean | undefined>
}) => {
  if (typeof window?.duet === 'undefined') return

  window.duet.unhide()

  window.duet.setUserId(userId)
  window.duet.setMetadata(metadata)
}

export const DecagonAiImpl = ({ userAttributes }: DecagonAiProps) => {
  const router = useRouter()
  const { widget, slug } = router.query

  const { setChatState } = useWidgetHandler({ widgetType: 'Decagon AI Chat' })

  const {
    isScriptLoaded,
    handleScriptLoad,
    waitingForExtraUserData,
    metadata,
    userId,
  } = useDecagon(userAttributes, slug as string)

  useEffect(() => {
    if (isScriptLoaded && !waitingForExtraUserData)
      setupChat({
        userId,
        metadata,
      })
  }, [userId, metadata, isScriptLoaded, waitingForExtraUserData])

  useEffect(() => {
    if (isScriptLoaded) {
      setTimeout(() => {
        if (typeof window?.duet !== 'undefined' && widget === 'true')
          window.duet.open()
      }, 2000)
    }
  }, [isScriptLoaded, widget])

  const handleEvent = useCallback(
    (event: { eventName: string }) => {
      if (event.eventName === 'DUET_INITIALIZE') {
        setChatState((state) => (state !== 'open-from-url' ? 'open' : state))
      }
      if (event.eventName === 'DUET_DEINITIALIZE') {
        setChatState('closed')
      }
    },
    [setChatState],
  )

  useEffect(() => {
    if (window.duet && isScriptLoaded && !waitingForExtraUserData) {
      window.duet.setEventListener(handleEvent)
    }
  }, [isScriptLoaded, waitingForExtraUserData, handleEvent])

  return (
    <Script
      src={DECAGON_AI_SCRIPT_SRC}
      strategy='lazyOnload'
      onLoad={handleScriptLoad}
      onError={(e) => {
        reportError(e, {
          description: 'There was an error loading the Decagon AI SDK.',
        })
      }}
    />
  )
}

export const DecagonAi = ({ userAttributes }: DecagonAiProps) => {
  const helpCenterAnonUuid = getHelpCenterAnonUuidCookie()
  const router = useRouter()
  const { slug } = router.query

  const brandKey = slug
    ? (brandKeysByRoute[slug as keyof typeof brandKeysByRoute] as BrandKey)
    : undefined

  // NOTE: If the script is loaded and then we client-side navigate to a product help center that should not show the chat for legal reasons, then the chat will hide but the script will still be present in the source of the page. It is important to use anchors instead of Next Links to navigate to product help centers to avoid this issue.
  const { shouldUseDecagon, isReady } = useChatAvailability(brandKey)

  // This useEffect acts as a safeguard to hide it in case client side navigation happens
  useEffect(() => {
    if (slug && !shouldUseDecagon) {
      window.duet?.hide()
    }
  }, [slug, shouldUseDecagon])

  if (!isReady || !shouldUseDecagon) {
    return null
  }

  // If the user is not logged in, we use the helpCenterAnonUuid instead of userId.
  // To ensure that the chat history of the logged-in user is not visible when logged out.
  // userId is anonId and could persist after logging out.
  const userAttributesWithAnonUuid = {
    ...userAttributes,
    userId: userAttributes?.is_logged_in
      ? userAttributes.userId
      : helpCenterAnonUuid,
  }

  return <DecagonAiImpl userAttributes={userAttributesWithAnonUuid} />
}
